import { formatUSPhoneNumber } from '@user/features/GlobalSearch/ResultItem/utils'
import { useMemo, useState } from 'react'
import { formatAddress } from '../../utils/formatAddress'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { EntityDetail } from '@user/store/map/typings'
import { getSalesFunnelStage } from '@user/features/MapView/MultiTenantDrawer/MultiTenantDrawerContentSingle/hooks/useData'

type Props = {
  entityDetails: EntityDetail | null
  serviceLocation: LocationDTO
  variant: string
  defaultOpen?: boolean
}

export function useData(props: Props) {
  const { entityDetails, serviceLocation, variant, defaultOpen } = props
  const [open, setOpen] = useState(defaultOpen ?? false)

  const email = useMemo(() => {
    if (entityDetails?.entityType === ENTITY_TYPES.DraftLead) {
      return entityDetails?.emailAddress
    } else {
      return entityDetails?.serviceContact?.emailAddresses[0]?.emailAddress
    }
  }, [entityDetails])

  const draftAddressDisplay = useMemo(() => {
    if (entityDetails?.entityType === ENTITY_TYPES.DraftLead) {
      const d = entityDetails
      return formatAddress(
        d.street1,
        d.street2,
        d.city,
        d.stateAbbreviation,
        d.postalCode,
      )
    }
    return 'xxx'
  }, [entityDetails])

  const contactAddressDisplay = useMemo(() => {
    const c = serviceLocation?.contact
    return c
      ? formatAddress(
          c.street1,
          c.street2,
          c.city,
          c.stateAbbreviation,
          c.postalCode,
        )
      : ''
  }, [serviceLocation])

  const addressDisplay =
    entityDetails?.entityType === ENTITY_TYPES.DraftLead
      ? draftAddressDisplay
      : contactAddressDisplay

  const formattedAltPhone = useMemo(() => {
    if (!serviceLocation || !serviceLocation.altPhoneNumber) {
      return ''
    }
    return formatUSPhoneNumber(serviceLocation.altPhoneNumber)
  }, [serviceLocation])

  const createdDateFormatted = useMemo(() => {
    const createdDate = entityDetails?.createdTime
    if (createdDate) {
      const date = new Date(createdDate)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      })
    }
    return ''
  }, [entityDetails])

  const entityTypeDisplay = useMemo(() => {
    switch (variant.toLowerCase()) {
      case 'lead':
        return 'Lead'
      case 'opportunity':
        return 'Opportunity'
      case 'draftlead':
      case 'pendinglead':
        return 'Pending Lead'
      default:
        return 'Unknown'
    }
  }, [variant])

  const formattedPhone = useMemo(() => {
    if (!serviceLocation) {
      return ''
    }
    const phone = serviceLocation.phoneNumber
    if (phone && formatUSPhoneNumber(phone).trim()) {
      return formatUSPhoneNumber(phone)
    } else return phone || ''
  }, [serviceLocation])

  const nameAndCompanyDisplay = useMemo(() => {
    if (!serviceLocation) {
      return ''
    }
    const companyName = serviceLocation.company
    const name = `${serviceLocation.firstName} ${serviceLocation.lastName}`
    const items = [name, companyName].filter((x) => x && x.trim().length > 0)
    if (items.length > 1) {
      return items.join(' / ')
    } else {
      return items[0] || ''
    }
  }, [serviceLocation])

  const entityType = useMemo(() => {
    switch (variant.toLowerCase()) {
      case 'lead':
        return 'Lead'
      case 'opportunity':
        return 'Opportunity'
      case 'draftlead':
        return 'Pending Lead'
      default:
        return 'Unknown'
    }
  }, [variant])

  function formatCurrency(
    amount: number,
    currency: string = 'USD',
    locale: string = 'en-US',
  ): string {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(amount)
  }

  const totalValueFormatted = useMemo(() => {
    const totalValue = entityDetails?.totalLeadValue
    if (totalValue) {
      return formatCurrency(totalValue, 'USD', 'en-US')
    }
    return '-'
  }, [entityDetails])

  const status = useMemo(() => {
    return getSalesFunnelStage(entityType, entityDetails?.salesFunnelStage)
  }, [entityType, entityDetails])

  return {
    createdDateFormatted,
    entityType,
    formattedAltPhone,
    formattedPhone,
    open,
    setOpen,
    addressDisplayName: addressDisplay,
    entityTypeDisplay,
    nameAndCompanyDisplay,
    totalValueFormatted,
    email,
    status,
    nextActionDate: entityDetails?.nextActionDate ?? null,
    id: entityDetails?.id ?? 0,
  }
}
